import { useQuery, useMutation, useQueryClient } from 'react-query';
import { message } from 'antd';
import { clinicsApi } from '../api';

// Query keys
export const CLINICS_QUERY_KEY = 'partner-clinics';
export const CLINIC_DETAIL_QUERY_KEY = 'clinic-detail';

// Get all clinics for a partner
export const useClinics = (partnerId, params = {}, options = {}) => {
  return useQuery(
    [CLINICS_QUERY_KEY, partnerId, params],
    () => clinicsApi.getByPartner(partnerId, params),
    {
      enabled: !!partnerId,
      staleTime: 30000,
      ...options,
    }
  );
};

// Get single clinic by ID
export const useClinic = (partnerId, clinicId, options = {}) => {
  return useQuery(
    [CLINIC_DETAIL_QUERY_KEY, partnerId, clinicId],
    () => clinicsApi.getById(partnerId, clinicId),
    {
      enabled: !!partnerId && !!clinicId,
      staleTime: 30000,
      ...options,
    }
  );
};

// Create clinic mutation
export const useCreateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, data }) => clinicsApi.create(partnerId, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICS_QUERY_KEY, variables.partnerId]);
        message.success('Clinic created successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to create clinic';
        message.error(errorMessage);
      },
    }
  );
};

// Update clinic mutation
export const useUpdateClinic = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, clinicId, data }) => clinicsApi.update(partnerId, clinicId, data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([CLINIC_DETAIL_QUERY_KEY, variables.partnerId, variables.clinicId]);
        message.success('Clinic updated successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update clinic';
        message.error(errorMessage);
      },
    }
  );
};

// Delete clinic mutation
export const useDeleteClinic = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, clinicId }) => clinicsApi.delete(partnerId, clinicId),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICS_QUERY_KEY, variables.partnerId]);
        message.success('Clinic deleted successfully');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to delete clinic';
        message.error(errorMessage);
      },
    }
  );
};

// Update clinic status mutation
export const useUpdateClinicStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, clinicId, status }) => clinicsApi.updateStatus(partnerId, clinicId, status),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([CLINIC_DETAIL_QUERY_KEY, variables.partnerId, variables.clinicId]);
        message.success('Clinic status updated successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update clinic status';
        message.error(errorMessage);
      },
    }
  );
};

// Update working hours mutation
export const useUpdateWorkingHours = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, clinicId, workingHours }) =>
      clinicsApi.updateWorkingHours(partnerId, clinicId, workingHours),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([CLINIC_DETAIL_QUERY_KEY, variables.partnerId, variables.clinicId]);
        message.success('Working hours updated successfully');
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update working hours';
        message.error(errorMessage);
      },
    }
  );
};